import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Inject,
  Optional,
  Type,
} from '@angular/core';
import {
  AuthenticationContextStore,
  AuthorizationStore,
  SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
  SOFTLINE_FEATURE_AUTHORIZATION,
} from '@softline/auth';
import { DateTimeTzService, isDefined, Store } from '@softline/core';
import {
  ChipComponent,
  ChipGroupComponent,
  IconComponent,
  TabComponent,
  TabGroupComponent,
} from '@softline/ui-core';
import { BehaviorSubject, combineLatestWith, interval, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { SOFTLINE_CONFIG_DASHBOARD_COMPONENT } from '../../application.shared';
import { isDashboardComponent } from '../types/dashboard-component';

@Component({
  selector: 'soft-dashboard',
  imports: [
    CommonModule,
    IconComponent,
    TabGroupComponent,
    TabComponent,
    ChipGroupComponent,
    ChipComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent {
  private readonly store: Store = inject(Store);
  private readonly dateTimeTzService = inject(DateTimeTzService);

  private _components$ = new BehaviorSubject<(Type<Component> | DashboardComponent)[]>(
    []
  );

  private readonly authContext = this.store.signal(
    SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
    AuthenticationContextStore.getters.data
  ) as any;

  private readonly user = computed(() => this.authContext()?.user);

  public readonly components$: Observable<Type<Component>[]> = this._components$.pipe(
    combineLatestWith(
      this.store.observe(
        SOFTLINE_FEATURE_AUTHORIZATION,
        AuthorizationStore.getters.permissions
      )
    ),
    map(([components, permissions]) =>
      components
        .map((o) => {
          if (!isDashboardComponent(o)) return o;
          if (!isDefined(o.permission) || permissions.indexOf(o.permission) > -1)
            return o.component;
          return undefined;
        })
        .filter((o): o is Type<Component> => !!o)
    )
  );

  readonly today$ = interval(1_000).pipe(
    startWith(0),
    map(() => new Date())
  );

  readonly greeting$ = this.today$.pipe(
    map((date) => {
      const hour = date.getHours();
      const name = this.authContext()?.userFullName ?? '';

      if (hour < 11) return `Guten Morgen, ${name}!`;
      if (hour < 14) return `Mahlzeit, ${name}!`;
      if (hour < 18) return `Hallo, ${name}!`;
      return `Guten Abend, ${name}!`;
    })
  );

  constructor(
    @Optional()
    @Inject(SOFTLINE_CONFIG_DASHBOARD_COMPONENT)
    components: (Type<Component> | DashboardComponent)[] = []
  ) {
    this._components$.next(components);
  }
}
